import React, { useEffect, useState, useCallback } from 'react';
import {
  Flame, Building2, MapPin, Clock, X, Filter, Heart, RotateCcw,
  Users, Briefcase, Mail, Linkedin, Search, ExternalLink, Copy,
  Check, Globe, Phone, ChevronDown, ChevronUp, Loader2, UserSearch,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar, CompanyTypeBadge } from './IntelligenceLayout';
import { WatchlistButton } from '../components/WatchlistButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------- Small Components ---------- */

const ScoreBadge = ({ score }) => {
  const max = 12;
  const pct = (score / max) * 100;
  let color = 'bg-gray-100 text-gray-600 border-gray-200';
  if (pct >= 75) color = 'bg-red-50 text-red-700 border-red-200';
  else if (pct >= 50) color = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (pct >= 25) color = 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold border ${color}`} data-testid="prospect-score-badge">
      <Flame className="h-3.5 w-3.5" />{score}/{max}
    </span>
  );
};

const SignalPills = ({ prospect }) => {
  const signals = [];
  if (prospect.high_volume) signals.push({ label: '5+ Jobs', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' });
  if (prospect.has_repeated_titles) signals.push({ label: 'Repeated Titles', color: 'bg-violet-50 text-violet-700 border-violet-200' });
  if (prospect.recent_activity) signals.push({ label: 'Recent', color: 'bg-blue-50 text-blue-700 border-blue-200' });
  if (prospect.multi_location) signals.push({ label: 'Multi-Location', color: 'bg-amber-50 text-amber-700 border-amber-200' });
  if (prospect.is_healthcare) signals.push({ label: 'Healthcare', color: 'bg-red-50 text-red-700 border-red-200' });
  return (
    <div className="flex flex-wrap gap-1">
      {signals.map(s => <span key={s.label} className={`px-2 py-0.5 rounded text-[10px] font-medium border ${s.color}`}>{s.label}</span>)}
    </div>
  );
};

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}>
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-gray-500" />}
      {copied ? 'Copied' : `Copy ${label}`}
    </button>
  );
};

/* ---------- Company Detail Modal ---------- */

const CompanyDetailModal = ({ company, prospect, onClose }) => {
  const [tab, setTab] = useState('overview');
  const [enrichData, setEnrichData] = useState(null);
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [enrichResp, postResp] = await Promise.all([
          fetchWithAuth(`${API_URL}/api/intelligence/company/${encodeURIComponent(company)}/enrich`),
          fetchWithAuth(`${API_URL}/api/intelligence/company/${encodeURIComponent(company)}/postings`),
        ]);
        setEnrichData(enrichResp);
        setPostings(postResp.postings || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [company]);

  const enrichment = enrichData?.enrichment;
  const decisionMakers = enrichData?.decision_makers || [];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'decision-makers', label: 'Decision Makers' },
    { id: 'outreach', label: 'Outreach' },
    { id: 'postings', label: `Postings (${postings.length})` },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="company-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            {enrichment?.logo_url && <img src={enrichment.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain bg-gray-50 border border-gray-100" />}
            <div>
              <h2 className="text-lg font-bold text-[#0A1F44]" data-testid="modal-company-name">{company}</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                {enrichment?.domain && <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{enrichment.domain}</span>}
                {enrichment?.industry && <span>{enrichment.industry}</span>}
                {enrichment?.employee_count && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{enrichment.employee_count.toLocaleString()} employees</span>}
                {enrichment?.revenue && <span>{enrichment.revenue} revenue</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {prospect && <ScoreBadge score={prospect.prospect_score} />}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="close-company-modal"><X className="h-5 w-5 text-gray-400" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === t.id ? 'border-[#D4AF37] text-[#0A1F44]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Enriching company data...
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {tab === 'overview' && (
                <div className="space-y-5">
                  {enrichment?.description && (
                    <p className="text-sm text-gray-600 leading-relaxed">{enrichment.description}</p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Active Postings', value: postings.length, icon: Briefcase },
                      { label: 'Founded', value: enrichment?.founded_year || '—', icon: Building2 },
                      { label: 'Location', value: [enrichment?.city, enrichment?.state].filter(Boolean).join(', ') || '—', icon: MapPin },
                      { label: 'Phone', value: enrichment?.phone || '—', icon: Phone },
                    ].map(m => (
                      <div key={m.label} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1"><m.icon className="h-3 w-3" />{m.label}</div>
                        <div className="text-sm font-semibold text-[#0A1F44]">{m.value}</div>
                      </div>
                    ))}
                  </div>
                  {enrichment?.departments && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Department Headcount</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(enrichment.departments).filter(([,v]) => v > 0).sort((a,b) => b[1]-a[1]).map(([k, v]) => (
                          <span key={k} className="px-2.5 py-1 rounded-lg text-xs bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                            {k.replace('_', ' ')}: {v}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {enrichment?.tech_stack?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Technology Stack</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {enrichment.tech_stack.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    {enrichment?.linkedin_url && (
                      <a href={enrichment.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-testid="company-linkedin-link">
                        <Linkedin className="h-3 w-3" /> LinkedIn
                      </a>
                    )}
                    {enrichment?.website && (
                      <a href={enrichment.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-testid="company-website-link">
                        <Globe className="h-3 w-3" /> Website
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Decision Makers Tab */}
              {tab === 'decision-makers' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">Find hiring decision makers at {company}. Click any title to search LinkedIn directly.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {decisionMakers.map((dm, i) => (
                      <a key={i} href={dm.linkedin_search_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
                        data-testid={`dm-link-${i}`}>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 bg-blue-50 rounded-lg flex items-center justify-center">
                            <UserSearch className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[#0A1F44]">{dm.title}</div>
                            <div className="text-[10px] text-gray-400">at {company}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Linkedin className="h-3 w-3" /> Search <ExternalLink className="h-3 w-3" />
                        </div>
                      </a>
                    ))}
                  </div>
                  {enrichment?.domain && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs text-amber-700">
                        <strong>Company Domain:</strong> {enrichment.domain} — Use this for email pattern matching (e.g., firstname.lastname@{enrichment.domain})
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Outreach Tab */}
              {tab === 'outreach' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-1.5"><Mail className="h-4 w-4" /> Outreach Email</h3>
                      <CopyButton text={enrichData?.outreach_email || ''} label="Email" />
                    </div>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-xl p-4 leading-relaxed font-sans" data-testid="outreach-email">
                      {enrichData?.outreach_email || 'Generating...'}
                    </pre>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-[#0A1F44] flex items-center gap-1.5"><Linkedin className="h-4 w-4" /> LinkedIn Message</h3>
                      <CopyButton text={enrichData?.linkedin_message || ''} label="LinkedIn Message" />
                    </div>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-blue-50 border border-blue-200 rounded-xl p-4 leading-relaxed font-sans" data-testid="linkedin-message">
                      {enrichData?.linkedin_message || 'Generating...'}
                    </pre>
                    <p className="text-[10px] text-gray-400 mt-1">LinkedIn connection messages have a 300 character limit. This message is optimized for that.</p>
                  </div>
                </div>
              )}

              {/* Postings Tab */}
              {tab === 'postings' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100">
                        <th className="text-left px-4 py-2">Job Title</th>
                        <th className="text-left px-4 py-2">Location</th>
                        <th className="text-left px-4 py-2">Industry</th>
                        <th className="text-left px-4 py-2">Type</th>
                        <th className="text-left px-4 py-2">Posted</th>
                        <th className="text-left px-4 py-2">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postings.map((p, i) => (
                        <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium text-[#0A1F44]">{p.raw_title}</td>
                          <td className="px-4 py-2.5 text-gray-600">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500">{p.industry || '—'}</td>
                          <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{p.employment_type}</span></td>
                          <td className="px-4 py-2.5 text-gray-500">{p.posting_date || '—'}</td>
                          <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">{p.source}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Main Page ---------- */

const IntelligenceProspects = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [sortField, setSortField] = useState('prospect_score');
  const [sortDir, setSortDir] = useState('desc');
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/hot-prospects?min_score=${minScore}&limit=750${filterParams}`)
      .then(data => setProspects(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [minScore, filterParams]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sorted = [...prospects].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    if (sortField === 'company') return mul * a.company.localeCompare(b.company);
    return mul * ((a[sortField] || 0) - (b[sortField] || 0));
  });

  const topScorers = prospects.filter(p => p.prospect_score >= 10).length;
  const healthcareCount = prospects.filter(p => p.is_healthcare).length;
  const recentCount = prospects.filter(p => p.recent_activity).length;

  const SortHeader = ({ field, label, align = 'left' }) => (
    <th className={`text-${align} px-4 py-3 cursor-pointer hover:text-[#0A1F44] select-none whitespace-nowrap`} onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  const openCompany = (prospect) => {
    setSelectedCompany(prospect.company);
    setSelectedProspect(prospect);
  };

  return (
    <IntelligenceLayout title="Hot Prospects">
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="hot-prospects-title">Hot Prospect Scoring</h1>
        <p className="text-sm text-gray-500">Companies with strong hiring activity — click any company for enrichment, decision makers, and outreach templates</p>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Analyzing company hiring activity...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="prospect-metrics">
              <MetricCard label="Total Prospects" value={prospects.length} icon={Building2} />
              <MetricCard label="Top Scorers (10+)" value={topScorers} icon={Flame} />
              <MetricCard label="Healthcare Targets" value={healthcareCount} icon={Heart} />
              <MetricCard label="Active This Week" value={recentCount} icon={Clock} />
            </div>

            <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing} excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />

            <div className="flex items-center gap-4" data-testid="prospect-filters">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600 font-medium">Min Score:</span>
              </div>
              {[0, 4, 6, 8, 10].map(s => (
                <button key={s} onClick={() => setMinScore(s)} data-testid={`filter-score-${s}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${minScore === s ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {s === 0 ? 'All' : `${s}+`}
                </button>
              ))}
              <span className="text-xs text-gray-400 ml-2">({sorted.length} companies)</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="prospects-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50">
                      <SortHeader field="company" label="Company" />
                      <SortHeader field="prospect_score" label="Score" align="center" />
                      <SortHeader field="total_postings" label="Active Jobs" align="right" />
                      <th className="text-left px-4 py-3">Top Role</th>
                      <th className="text-left px-4 py-3">Locations</th>
                      <SortHeader field="latest_posting" label="Last Posted" />
                      <th className="text-left px-4 py-3">Signals</th>
                      <th className="text-center px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-400">No prospects match the current filter.</td></tr>
                    ) : sorted.map((p, i) => (
                      <tr key={p.company} className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors" data-testid={`prospect-row-${i}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <WatchlistButton company={p.company} sourceModule="hot-prospects" />
                            <button onClick={() => openCompany(p)} className="text-[#0A1F44] font-medium hover:text-[#D4AF37] hover:underline transition-colors text-left" data-testid={`company-link-${i}`}>
                              {p.company}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center"><ScoreBadge score={p.prospect_score} /></td>
                        <td className="px-4 py-3 text-right font-semibold text-[#0A1F44]">{p.total_postings}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{p.most_common_title || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(p.locations || []).slice(0, 2).map((loc, j) => (
                              <span key={j} className="inline-flex items-center gap-0.5 text-xs text-gray-500"><MapPin className="h-3 w-3 text-gray-400" />{loc}</span>
                            ))}
                            {(p.locations || []).length > 2 && <span className="text-xs text-gray-400">+{p.locations.length - 2}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-sm">{p.latest_posting || '—'}</td>
                        <td className="px-4 py-3"><SignalPills prospect={p} /></td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => openCompany(p)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                            data-testid={`enrich-btn-${i}`}>
                            <Search className="h-3 w-3" /> Enrich
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="scoring-legend">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Scoring Criteria (Max 12 pts)</h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {[
                  { label: '5+ Active Postings', pts: '+3', icon: Users, color: 'text-emerald-600' },
                  { label: 'Repeated Job Titles', pts: '+3', icon: RotateCcw, color: 'text-violet-600' },
                  { label: 'Posted Within 7 Days', pts: '+2', icon: Clock, color: 'text-blue-600' },
                  { label: 'Multi-Location Hiring', pts: '+2', icon: MapPin, color: 'text-amber-600' },
                  { label: 'Healthcare / Clinical', pts: '+2', icon: Heart, color: 'text-red-600' },
                ].map(c => (
                  <div key={c.label} className="flex items-center gap-2 text-sm">
                    <c.icon className={`h-4 w-4 ${c.color}`} />
                    <span className="text-gray-600">{c.label}</span>
                    <span className="ml-auto font-bold text-[#0A1F44]">{c.pts}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          prospect={selectedProspect}
          onClose={() => { setSelectedCompany(null); setSelectedProspect(null); }}
        />
      )}
    </IntelligenceLayout>
  );
};

export default IntelligenceProspects;
